import { useEffect, useState } from "preact/hooks";
import { idx, unitColor, flash } from "../store";
import { isPicked, togglePick, active } from "../lib/state";
import { loadDetails } from "../lib/data";
import { personKey } from "../lib/text";
import { DAYS } from "../lib/planner";
import { day, kindLabel, t } from "../i18n";
import type { Course, CourseDetail, Lecturer } from "../lib/types";
import { ContactsDialog } from "./dialogs/Contacts";
import { SuggestDialog } from "./dialogs/Suggest";

const SYL: [keyof NonNullable<CourseDetail["syllabus"]>, string, boolean?][] = [
  ["goals", "Cilj predmeta · objectives", true], ["topics", "Tematske jedinice · topics", true], ["outcomes", "Ishodi učenja · learning outcomes"],
  ["methods", "Metode izvođenja nastave · teaching"], ["assessment", "Provjera znanja i ocjenjivanje · assessment"], ["literature", "Literatura · reading list"],
  ["prerequisites", "Preduslov za upis · prerequisites"], ["participants", "Učesnici u nastavi · teaching staff"], ["notes", "Napomena · notes"],
];

export function teachersOf(c: Course): Lecturer[] {
  const I = idx.value!;
  const byKey = new Map<string, Lecturer>();
  const peopleByKey = new Map(I.core.people.map((p) => [personKey(p.name), p]));
  for (const l of c.lecturers || []) { const k = personKey(l.name); if (k && (!byKey.has(k) || (!byKey.get(k)!.email && l.email))) byKey.set(k, { ...l }); }
  for (const s of I.sessionsByCourse.get(c.id) ?? []) {
    if (!s.lecturer) continue;
    for (const nm of s.lecturer.split(/[,;/]| i (?=[A-ZČĆŠĐŽ])/)) {
      const k = personKey(nm);
      if (k && !byKey.has(k)) byKey.set(k, { name: nm.trim(), email: null, role: s.kind === "P" ? "professor" : "assistant" });
    }
  }
  for (const [k, l] of byKey) { const p = peopleByKey.get(k); if (p) { l.email ||= p.email; l.email_verified ??= p.email_verified; } }
  return [...byKey.values()];
}

export function Email({ l }: { l: Lecturer }) {
  if (!l.email) return <span class="email note">email nije pronađen</span>;
  return (
    <span class="email">
      <a href={`mailto:${l.email}`}>{l.email}</a>
      {l.email_verified === false && <span class="guess" title="Izvedeno iz obrasca adresa fakulteta, nije pronađeno na službenoj stranici">pretpostavljeno</span>}
      <button class="copy" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(l.email!); flash("Kopirano"); }}>copy</button>
    </span>
  );
}

export function CourseView({ id, inDrawer }: { id: string; inDrawer?: boolean }) {
  const I = idx.value!;
  const c = I.courses.get(id) ?? I.courses.get(I.core.aliases[id]);
  const [det, setDet] = useState<CourseDetail | null>(null);
  const [dialog, setDialog] = useState<"contacts" | "suggest" | null>(null);
  useEffect(() => { setDet(null); if (c) loadDetails(c.fac).then((d) => setDet(d[c.id] ?? {})); }, [id]);
  if (!c) return <p class="empty">Predmet nije pronađen.</p>;
  active.value;
  const unit = I.units.get(c.fac);
  const lect = teachersOf(c);
  const sess = [...(I.sessionsByCourse.get(c.id) ?? [])].sort((a, b) => a.kind.localeCompare(b.kind) || DAYS.indexOf(a.day) - DAYS.indexOf(b.day) || a.start.localeCompare(b.start));
  const facts: [string, unknown][] = ([
    ["Fakultet", unit?.name ?? c.fac], ["Odsjek", c.department], ["Programi", c.programs?.join(", ")], ["Ciklus", c.cycle], ["Godina", c.year],
    ["Semestar", c.semester || (c.term === "winter" ? "zimski" : c.term === "summer" ? "ljetni" : "")], ["ECTS", c.ects],
    ["Status", c.obligatory === true ? "obavezni" : c.obligatory === false ? "izborni" : ""], ["Šifra", c.code],
    ["Sati / sedm.", c.hours ? Object.entries(c.hours).filter(([, v]) => v).map(([k, v]) => `${v}${k}`).join(" + ") : ""],
  ] as [string, unknown][]).filter(([, v]) => v !== undefined && v !== null && v !== "");
  const syl = det?.syllabus;
  return (
    <div class="cview" style={`--c:${unitColor(c.fac)}`}>
      <div class="kicker"><a href={`/u/${c.fac}`} style="text-decoration:none">{unit?.name ?? c.fac}</a></div>
      <h2>{c.name}</h2>
      {c.name_en && c.name_en !== c.name && <p class="en">{c.name_en}</p>}
      <div class="facts">{facts.map(([k, v]) => <div key={k}><span>{k}</span><b>{String(v)}</b></div>)}</div>
      {c.description && <><h5>O predmetu</h5><p class="desc">{c.description}</p></>}
      {det?.notes && <p class="note" style="overflow-wrap:anywhere">{det.notes}</p>}

      {syl && (
        <>
          <h5>Silabus {syl._pages && `· PDF str. ${syl._pages[0]}–${syl._pages[1]}`}</h5>
          <div class="syl">
            {SYL.filter(([k]) => syl[k]).map(([k, label, open]) => (
              <details key={k} open={open}><summary>{label}</summary><div class="syl-body">{String(syl[k])}</div></details>
            ))}
            <p class="note">Prepisano OCR-om sa skeniranog službenog silabusa, pa su moguće manje greške.{det?.url && syl._pages && <> <a href={`${det.url}#page=${syl._pages[0]}`} target="_blank" rel="noopener">otvori sken ↗</a></>}</p>
          </div>
        </>
      )}

      <h5>Nastavnici</h5>
      <div class="people">
        {lect.length ? lect.map((l) => (
          <div class="person" key={l.name}>
            <div><a href={`/t/${encodeURIComponent(personKey(l.name).replace(/ /g, "-"))}`} style="text-decoration:none">{l.name}</a><span class="role">{l.role === "assistant" ? "saradnik" : l.role === "professor" ? "nastavnik" : l.role || ""}</span></div>
            <Email l={l} />
          </div>
        )) : <p class="note">Nastavnici nisu navedeni u izvorima.</p>}
      </div>

      <h5>Raspored {sess.length ? `· ${[...new Set(sess.map((s) => s.academic_year))].join(", ")}` : ""}</h5>
      {sess.length ? (
        <table class="sess">
          <thead><tr><th>Tip</th><th>Grupa</th><th>Termin</th><th>Sala</th><th>Nastavnik</th></tr></thead>
          <tbody>{sess.map((s) => (
            <tr key={s.key}>
              <td><span class="kind" title={kindLabel(s.kind)}>{s.kind}</span></td>
              <td>{s.group || s.program || "-"}</td>
              <td class="mono">{day(s.day)} {s.start}–{s.end}{s.weeks && s.weeks !== "every" && <><br /><span class="note">{s.weeks}</span></>}{s.overrides && <><br /><span class="note" title={s.overrides.map((o) => o.note || o.source).join("\n")}>✎ ispravljeno</span></>}</td>
              <td>{s.room && <a href={`/r/${c.fac}/${encodeURIComponent(s.room)}`}>{s.room}</a>}</td>
              <td>{s.lecturer}</td>
            </tr>
          ))}</tbody>
        </table>
      ) : <p class="note">Za ovaj predmet nismo pronašli termine.</p>}

      <div class="cta">
        <button class="btn" onClick={() => { togglePick(c.id, c.term); flash(isPicked(c.id) ? "Dodano u sedmicu" : "Uklonjeno"); }}>{isPicked(c.id) ? t("remove") : t("add")}</button>
        {lect.some((l) => l.email) && <button class="btn ghost" onClick={() => setDialog("contacts")}>Email nastavnicima</button>}
        <button class="btn ghost" onClick={() => setDialog("suggest")}>{t("suggest")}</button>
        {inDrawer && <a class="btn ghost" style="text-decoration:none" href={`/c/${encodeURIComponent(c.id)}`}>Stranica predmeta ↗</a>}
      </div>
      {dialog === "contacts" && <ContactsDialog ids={[c.id]} onClose={() => setDialog(null)} />}
      {dialog === "suggest" && <SuggestDialog course={c} onClose={() => setDialog(null)} />}
    </div>
  );
}
